// Security blocks list. Admin-driven CVE response surface.

import { Link, useNavigate } from 'react-router-dom';
import { ShieldAlert, Plus, Trash2, Pause, Play, ExternalLink } from 'lucide-react';
import { SecurityShell } from '../../components/SecurityShell';
import { Card, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import {
  useSecurityBlocks,
  deleteSecurityBlock,
  toggleSecurityBlock,
  type SecurityBlock,
} from '../../api/security';

function blockTypeTone(t: SecurityBlock['block_type']): 'accent' | 'nebula' | 'warning' | 'danger' {
  switch (t) {
    case 'sha256': return 'accent';
    case 'pattern': return 'nebula';
    case 'package': return 'warning';
    case 'path': return 'danger';
  }
}

function fmtDate(s?: string | null): string {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

export function SecurityList() {
  const { data: blocks, mutate, isLoading } = useSecurityBlocks('*');
  const navigate = useNavigate();

  const onDelete = async (id: string) => {
    if (!confirm('Permanently delete this block? Audit rows will keep referencing it as "(block deleted)".')) return;
    await deleteSecurityBlock(id);
    await mutate();
  };

  const onToggle = async (id: string, active: boolean) => {
    await toggleSecurityBlock(id, !active);
    await mutate();
  };

  return (
    <SecurityShell
      eyebrow="Enforcement · supply-chain"
      title="Security blocks"
      description={
        <>
          Block specific artifacts or whole packages from being downloaded.
          Use this for CVE-driven supply-chain incidents — when an upstream
          version is malicious or has just landed on a known-bad list,
          block it here and every fetch attempt across the platform
          returns 403 with the configured reason.
        </>
      }
    >
      <div className="flex justify-end">
        <Button variant="primary" onClick={() => navigate('/security/new')}>
          <Plus className="h-4 w-4" />
          New block
        </Button>
      </div>

      <Card>
        <CardHeader
          title={`All blocks (${blocks?.length ?? 0})`}
          description="Active rows are enforced by middleware on every request. Toggle a row off to disable without losing the rule (audit history is preserved either way)."
          actions={isLoading ? <Spinner size="xs" /> : null}
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-5 py-3 font-medium text-fg-muted">Status</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Type</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Pattern</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Reason</th>
                <th className="px-5 py-3 font-medium text-fg-muted">CVE / advisory</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Until</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Created by</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {blocks?.length ? (
                blocks.map((b) => (
                  <tr key={b.id} className="border-b border-border/60 last:border-0 hover:bg-bg-hover/40">
                    <td className="px-5 py-3">
                      {b.active ? (
                        <Badge tone="danger">
                          <ShieldAlert className="h-3 w-3" />
                          Active
                        </Badge>
                      ) : (
                        <Badge tone="neutral">Paused</Badge>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <Badge tone={blockTypeTone(b.block_type)}>{b.block_type}</Badge>
                    </td>
                    <td className="px-5 py-3 font-mono text-xs text-fg break-all max-w-sm">{b.pattern}</td>
                    <td className="px-5 py-3 text-fg-muted max-w-md">{b.reason || <span className="italic text-fg-subtle">—</span>}</td>
                    <td className="px-5 py-3">
                      {b.cve_id ? (
                        <span className="orb-code">{b.cve_id}</span>
                      ) : <span className="text-fg-subtle">—</span>}
                    </td>
                    <td className="px-5 py-3 text-fg-muted text-xs whitespace-nowrap">{fmtDate(b.blocked_until)}</td>
                    <td className="px-5 py-3 text-fg-muted text-xs">{b.created_by || '—'}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <a
                          href={`/blocked/${b.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="View public block-details page"
                          className="h-8 w-8 inline-flex items-center justify-center rounded-md text-fg-muted hover:bg-bg-hover hover:text-fg"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                        <button
                          type="button"
                          title={b.active ? 'Pause' : 'Resume'}
                          onClick={() => onToggle(b.id, b.active)}
                          className="h-8 w-8 inline-flex items-center justify-center rounded-md text-fg-muted hover:bg-bg-hover hover:text-fg"
                        >
                          {b.active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                        </button>
                        <button
                          type="button"
                          title="Delete"
                          onClick={() => onDelete(b.id)}
                          className="h-8 w-8 inline-flex items-center justify-center rounded-md text-fg-muted hover:bg-danger/10 hover:text-danger"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="px-5 py-12 text-center text-fg-muted text-sm">
                    No blocks defined. Click <Link to="/security/new" className="text-accent hover:underline">New block</Link> to add one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </SecurityShell>
  );
}
